/** Creating and copying PlayerState values. */
import type { LevelStart } from '../level/types';
import type { PlayerState } from './types';

const NONE_USED: readonly number[] = Object.freeze([]);

/**
 * A fresh, airborne state at the level's spawn point. Prefer `spawnPlayer` from
 * physics.ts, which also detects whether the spawn point rests on the ground.
 */
export function createPlayerState(start: LevelStart): PlayerState {
  return {
    tick: 0,
    x: start.x,
    y: start.y,
    vy: 0,
    mode: start.mode,
    gravity: start.gravity,
    speed: start.speed,
    grounded: false,
    headContact: false,
    held: false,
    pressAge: -1,
    coyote: 0,
    thrust: 0,
    airJumps: 0,
    settle: 0,
    used: NONE_USED,
    alive: true,
    finished: false,
    deathCause: null,
  };
}

/** Independent copy (the `used` list is immutable and therefore shared). */
export function clonePlayerState(state: PlayerState): PlayerState {
  return { ...state };
}

/**
 * True if two states are identical in everything that shapes the rest of the run
 * (`used` compared as a set). With the same inputs from here on, such runs stay equal.
 */
export function samePlayerState(a: Readonly<PlayerState>, b: Readonly<PlayerState>): boolean {
  return (
    a.tick === b.tick &&
    a.x === b.x &&
    a.y === b.y &&
    a.vy === b.vy &&
    a.mode === b.mode &&
    a.gravity === b.gravity &&
    a.speed === b.speed &&
    a.grounded === b.grounded &&
    a.headContact === b.headContact &&
    a.held === b.held &&
    a.pressAge === b.pressAge &&
    a.coyote === b.coyote &&
    a.thrust === b.thrust &&
    a.airJumps === b.airJumps &&
    a.settle === b.settle &&
    a.alive === b.alive &&
    a.finished === b.finished &&
    a.deathCause === b.deathCause &&
    a.used.length === b.used.length &&
    a.used.every((id) => b.used.includes(id))
  );
}
